import api from "../../Environment";

/**
 * A-008 — Commerce cart API. React consumes only cartPayload() from `data`.
 * No client-side pricing, discounts, taxes, or shipping math.
 */

const GUEST_TOKEN_KEY = "commerce_guest_token";

export function getGuestToken() {
  try {
    return localStorage.getItem(GUEST_TOKEN_KEY) || "";
  } catch (e) {
    return "";
  }
}

export function persistGuestToken(token) {
  if (!token) return;
  try {
    localStorage.setItem(GUEST_TOKEN_KEY, token);
  } catch (e) {
    /* ignore */
  }
}

function emptyCart() {
  return {
    guest_token: getGuestToken() || null,
    currency: "USD",
    items: [],
    summary: {
      subtotal: 0,
      discounts: 0,
      taxes: null,
      shipping: null,
      total: 0,
    },
    coupon: null,
    warnings: [],
    messages: [],
  };
}

function unwrapCart(response) {
  const body = response && response.data;
  if (!body || body.success === false) {
    const err = new Error(
      (body && (body.error_message || body.error_messages)) ||
        "Cart request failed"
    );
    err.code = body && body.error_code;
    throw err;
  }
  const cart = body.data || emptyCart();
  if (cart.guest_token) {
    persistGuestToken(cart.guest_token);
  }
  return cart;
}

function withGuest(params = {}) {
  const guest = getGuestToken();
  if (guest) {
    return { ...params, guest_token: guest };
  }
  return { ...params };
}

export async function fetchCart() {
  try {
    const response = await api.postMethod("commerce/cart", withGuest({}));
    return unwrapCart(response);
  } catch (e) {
    if (e && e.message && !e.code) {
      return emptyCart();
    }
    throw e;
  }
}

export async function addCartItem({
  purchasableType = "shop_product",
  purchasableId,
  quantity = 1,
} = {}) {
  const response = await api.postMethod(
    "commerce/cart/add",
    withGuest({
      purchasable_type: purchasableType,
      purchasable_id: purchasableId,
      quantity,
    })
  );
  return unwrapCart(response);
}

export async function removeCartItem(itemId) {
  const response = await api.postMethod(
    "commerce/cart/remove",
    withGuest({ item_id: itemId })
  );
  return unwrapCart(response);
}

export async function updateCartQuantity(itemId, quantity) {
  const response = await api.postMethod(
    "commerce/cart/update",
    withGuest({ item_id: itemId, quantity })
  );
  return unwrapCart(response);
}

export async function applyCartCoupon(couponCode) {
  const response = await api.postMethod(
    "commerce/cart/coupon",
    withGuest({ coupon_code: couponCode })
  );
  return unwrapCart(response);
}

export async function removeCartCoupon() {
  const response = await api.postMethod(
    "commerce/cart/coupon/remove",
    withGuest({})
  );
  return unwrapCart(response);
}

/** @deprecated A-009B — Prefer summary.formatted / pricing.formatted from payloads. */
export function formatCartMoney(amount, currency = "USD") {
  if (amount === null || amount === undefined || amount === "") {
    return null;
  }
  const n = Number(amount);
  if (!Number.isFinite(n)) {
    return null;
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    }).format(n);
  } catch (e) {
    return `$${n.toFixed(2)}`;
  }
}
